/**
 * Page 1 of Form 2848 (Rev. January 2021), replicated.
 *
 * Layout is absolute: the official is a fixed-layout page, so matching it
 * means placing things, not flowing them. Every x/y is a point coordinate
 * measured out of the official PDF, and every rule and checkbox comes from
 * f2848-rules.ts, extracted from the same render.
 *
 * `fit` carries each run's measured ink width so the substituted face is
 * scaled to the official's line widths — see At in f2848-layout.tsx.
 *
 * ---------------------------------------------------------------------------
 * Two places where these numbers are NOT the official's
 * ---------------------------------------------------------------------------
 * Both are deliberate, and both are about the sheet being read on screen and
 * on a printed capture rather than admired as a facsimile.
 *
 * 1. Checkboxes take the y of the run they read with, not their own measured
 *    top. The official sets a box like a character on the baseline — 8pt tall
 *    on 8pt type — so it reaches 2.3pt above the caps and stops level with the
 *    baseline, which reads high, with more white under it than over it. `Cb`
 *    now centres itself on the middle of the run's x-height; see the note on
 *    it in
 *    f2848-layout.tsx.
 *
 * 2. Rows whose descenders landed on the rule beneath them are lifted. Some
 *    the official runs past the rule outright: the representative panel's
 *    "CAF No." caption descends to y=216.6 against its own write-in rule at
 *    y=216. The lift is
 *    1.5pt for 8pt rows and 1.0pt for 7pt, which clears the rule below by
 *    ~1.4pt while leaving the checkbox ~1.4pt under the rule above; it is as
 *    much as the 12pt rows have room for. Boxes follow their run, so they stay
 *    centred.
 *
 *    The "For IRS Use Only" Date row is its own case: the official gives the
 *    9pt slashes the same baseline as the 7pt "Date", but a slash's ink reaches
 *    0.731em where 7pt caps reach 0.714 x 7, so at a shared baseline they ride
 *    ~0.9pt high. They get their own baseline instead, 0.87pt lower, which puts
 *    the two ink centres within 0.2pt of each other.
 */
import { At, Arrow, Cb, Field, Leader, Rules, pt, FONT_WORDMARK, FONT_TITLE } from "./f2848-layout";
import { H_SOLID_P1, H_DASHED_P1, V_RULES_P1 } from "./f2848-rules";

interface PageProps {
  value?: any;
  onChange?: (patch: any) => void;
  disabled?: boolean;
}

const F2848Page1 = ({ value, onChange, disabled }: PageProps) => {
  const v = value || {};
  const set =
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange?.({ [name]: e.target.value });

  const taxpayerLines = [
    `${v?.extracted_data?.name || ""} ${v?.extracted_data?.surname || ""}`.trim(),
    v?.address,
    [v?.city, v?.state, v?.zip_code].filter(Boolean).join(", "),
  ].filter(Boolean) as string[];

  /**
   * One representative block. `agent` fills the first one with the practice's
   * own details, matching what the previous Profile_container rendered with
   * isValue={true}; representatives 2-4 are left blank as before.
   */
  const Rep = ({ y, note, agent }: { y: number; note?: boolean; agent?: boolean }) => (
    <>
      <At x={36} y={y} size={8} fit={67.9}>Name and address</At>
      {agent ? (
        <>
          <Field x={36} y={y + 11} w={290} h={11} value={v?.repName ?? "Theitin.com"} onChange={set("repName")} disabled={disabled} />
          <Field x={36} y={y + 23} w={290} h={11} value={v?.repAddress ?? "1307 THORNHILL DR, NEWARK, DE 19702"} onChange={set("repAddress")} disabled={disabled} />
          <Field x={390} y={y + 0.5} w={140} h={10} value={v?.repCaf ?? "0313-86815R"} onChange={set("repCaf")} disabled={disabled} />
          <Field x={390} y={y + 12.5} w={140} h={10} value={v?.repPtin ?? "#P02125927"} onChange={set("repPtin")} disabled={disabled} />
          <Field x={415} y={y + 24.5} w={115} h={10} value={v?.repPhone ?? "+1 470 220 30 33"} onChange={set("repPhone")} disabled={disabled} />
          <Field x={390} y={y + 36.5} w={140} h={10} value={v?.repFax ?? "+1 442 229 6661"} onChange={set("repFax")} disabled={disabled} />
        </>
      ) : null}
      <At x={353} y={y - 0.5} size={8} fit={30.2}>CAF No.</At>
      <At x={353} y={y + 11.5} size={8} fit={17.4}>PTIN</At>
      <At x={353} y={y + 23.5} size={8} fit={52.1}>Telephone No.</At>
      <At x={353} y={y + 35.5} size={8} fit={27.7}>Fax No.</At>
      {note ? (
        <>
          <At x={38.04} y={y + 166.5} size={8} bold fit={20.1}>Note:</At>
          <At x={36} y={y + 167.5} size={8}>(</At>
          <At x={60.37} y={y + 167.5} size={8} fit={241.1}>
            IRS sends notices and communications to only two representatives.)
          </At>
        </>
      ) : null}
    </>
  );

  return (
    <>
      <Rules
        h={H_SOLID_P1}
        v={V_RULES_P1}
        dashed={H_DASHED_P1}
        /* the only rules the official draws heavier than a 0.5pt hairline:
           the footer rule and the masthead's OMB divider */
        hWeights={{ 744: 1 }}
        vWeights={{ 489: 1 }}
      />

      {/* ============================ masthead ============================ */}
      <At x={36} y={47} size={7} fit={16.3}>Form</At>
      <At
        x={58.6}
        y={31}
        size={24}
        fit={49.5}
        leading={24}
        style={{ fontFamily: FONT_WORDMARK, fontWeight: 400 }}
      >
        2848
      </At>
      <At x={36} y={57} size={7} fit={61.8}>(Rev. January 2021)</At>
      <At x={36} y={67} size={7} fit={81.5}>Department of the Treasury</At>
      <At x={36} y={74} size={7} fit={73.8}>Internal Revenue Service</At>

      <At x={250} y={36} size={14} fit={112.4} leading={14} style={{ fontFamily: FONT_TITLE }}>
        Power of Attorney
      </At>
      <At x={199} y={51} size={14} fit={214.0} leading={14} style={{ fontFamily: FONT_TITLE }}>
        and Declaration of Representative
      </At>
      <Arrow x={166} y={73} size={5} />
      <At x={174} y={71} size={8} bold fit={274.0}>
        Go to <i>www.irs.gov/Form2848</i> for instructions and the latest
        information.
      </At>

      <At x={500} y={37} size={7} fit={65.3}>OMB No. 1545-0150</At>
      <At x={504} y={50} size={7} bold fit={57.1}>For IRS Use Only</At>
      <At x={497} y={62} size={7} fit={40.4}>Received by:</At>
      <At x={497} y={75} size={7} fit={18.3}>Name</At>
      <At x={497} y={87} size={7} fit={32.6}>Telephone</At>
      <At x={497} y={99} size={7} fit={27.0}>Function</At>
      <At x={497} y={110.25} size={7} fit={14.4}>Date</At>
      <At x={539} y={109.2} size={9} fit={20.7} style={{ whiteSpace: "pre" }}>
        {"/      /"}
      </At>

      {/* ============================= Part I ============================= */}
      <div
        style={{
          position: "absolute",
          left: pt(36),
          top: pt(83.75),
          width: pt(35.75),
          height: pt(12.5),
          background: "#000",
        }}
      />
      <At x={41} y={83} size={10} bold fit={25.8} style={{ color: "#fff" }}>
        Part I
      </At>
      <At x={86} y={84} size={10} bold fit={86.3}>Power of Attorney</At>

      <At x={86} y={97} size={8.5} bold fit={34}>Caution:</At>
      <At x={122.54} y={98} size={8.5} fit={352.7}>
        A separate Form 2848 must be completed for each taxpayer. Form 2848 will
        not be honored
      </At>
      <At x={86.03} y={108} size={8.5} fit={217.4}>
        for any purpose other than representation before the IRS.
      </At>

      {/* =============================== 1 ================================ */}
      <At x={46} y={121} size={8} bold>1</At>
      <At x={65} y={121} size={8} bold fit={83.2}>Taxpayer information.</At>
      <At x={150} y={121} size={8} fit={201.7}>
        Taxpayer must sign and date this form on page 2, line 7.
      </At>

      <At x={36} y={133} size={8} fit={101.8}>Taxpayer name and address</At>
      {/* The cell is 34pt tall — the official is filled as stacked lines, so
          the value is split rather than run on and clipped at the divider. */}
      <Field
        x={36}
        y={147}
        w={298}
        h={11}
        name="taxpayerName"
        value={taxpayerLines[0] || ""}
        onChange={set("taxpayerName")}
        disabled={disabled}
      />
      <Field
        x={36}
        y={158}
        w={298}
        h={11}
        name="taxpayerAddress"
        value={taxpayerLines[1] || ""}
        onChange={set("taxpayerAddress")}
        disabled={disabled}
      />
      <Field
        x={36}
        y={169}
        w={298}
        h={11}
        name="taxpayerCityStateZip"
        value={taxpayerLines[2] || ""}
        onChange={set("taxpayerCityStateZip")}
        disabled={disabled}
      />
      <At x={342} y={133} size={8} fit={119.1}>
        Taxpayer identification number(s)
      </At>
      <Field
        x={342}
        y={143}
        w={232}
        h={12}
        name="taxpayerTin"
        value={v?.taxpayerTin ?? "Applied For"}
        onChange={set("taxpayerTin")}
        disabled={disabled}
      />
      <At x={342} y={157} size={8} fit={97.0}>Daytime telephone number</At>
      <Field x={342} y={167} w={119} h={12} disabled />
      <At x={465} y={157} size={8} fit={94.6}>Plan number (if applicable)</At>
      <Field x={465} y={167} w={109} h={12} disabled />

      <At x={36} y={181} size={8} fit={245.1}>
        hereby appoints the following representative(s) as attorney(s)-in-fact:
      </At>

      {/* =============================== 2 ================================ */}
      <At x={46} y={193} size={8} bold>2</At>
      <At x={65} y={193} size={8} bold fit={66.5}>Representative(s)</At>
      <At x={134} y={193} size={8} fit={167.8}>
        must sign and date this form on page 2, Part II.
      </At>

      <Rep y={205} agent />
      <At x={36} y={253} size={8} bold fit={221.4}>
        Check if to be sent copies of notices and communications
      </At>
      <Cb x={273.6} y={253} weight={1.5} />
      <At x={312.13} y={253} size={8} fit={82.7}>Check if new:  Address</At>
      <Cb x={398.4} y={253} />
      <At x={422.9} y={253} size={8} fit={52.3}>Telephone No.</At>
      <Cb x={478.8} y={253} />
      <At x={515.75} y={253} size={8} fit={27.9}>Fax No.</At>
      <Cb x={547.2} y={253} />

      <Rep y={265} />
      <At x={36} y={313} size={8} bold fit={221.4}>
        Check if to be sent copies of notices and communications
      </At>
      <Cb x={273.6} y={313} weight={1.5} />
      <At x={312.13} y={313} size={8} fit={82.7}>Check if new:  Address</At>
      <Cb x={398.4} y={313} />
      <At x={422.9} y={313} size={8} fit={52.3}>Telephone No.</At>
      <Cb x={478.8} y={313} />
      <At x={515.75} y={313} size={8} fit={27.9}>Fax No.</At>
      <Cb x={547.2} y={313} />

      <Rep y={325} />
      <At x={38} y={371.5} size={8} bold fit={20.2}>Note:</At>
      <At x={36} y={372.5} size={8}>(</At>
      <At x={60} y={372.5} size={8} fit={241.5}>
        IRS sends notices and communications to only two representatives.)
      </At>
      <At x={312.13} y={373} size={8} fit={82.7}>Check if new:  Address</At>
      <Cb x={398.4} y={373} />
      <At x={422.9} y={373} size={8} fit={52.3}>Telephone No.</At>
      <Cb x={478.8} y={373} />
      <At x={515.75} y={373} size={8} fit={27.9}>Fax No.</At>
      <Cb x={547.2} y={373} />

      <Rep y={385} />
      <At x={38} y={431.5} size={8} bold fit={20.2}>Note:</At>
      <At x={36} y={432.5} size={8}>(</At>
      <At x={60} y={432.5} size={8} fit={241.5}>
        IRS sends notices and communications to only two representatives.)
      </At>
      <At x={312.13} y={433} size={8} fit={82.7}>Check if new:  Address</At>
      <Cb x={398.4} y={433} />
      <At x={422.9} y={433} size={8} fit={52.3}>Telephone No.</At>
      <Cb x={478.8} y={433} />
      <At x={515.75} y={433} size={8} fit={27.9}>Fax No.</At>
      <Cb x={547.2} y={433} />

      <At x={36} y={445} size={8} fit={332.9}>
        to represent the taxpayer before the Internal Revenue Service and
        perform the following acts:
      </At>

      {/* =============================== 3 ================================ */}
      <At x={46} y={459} size={8} bold>3</At>
      <At x={65} y={459} size={8} bold fit={200.9}>
        Acts authorized (you are required to complete line 3).
      </At>
      <At x={268} y={459} size={8} fit={308.0}>
        Except for the acts described in line 5b, I authorize my
        representative(s) to receive and
      </At>
      <At x={65} y={470} size={8} fit={510.9}>
        inspect my confidential tax information and to perform acts I can
        perform with respect to the tax matters described below. For example, my
      </At>
      <At x={65} y={481} size={8} fit={510.9}>
        representative(s) shall have the authority to sign any agreements,
        consents, or similar documents (see instructions for line 5a for
        authorizing a
      </At>
      <At x={65} y={492} size={8} fit={109.9}>
        representative to sign a return).
      </At>

      {/* line 3 table headings */}
      <At x={40} y={507} size={8} fit={258.5}>
        Description of Matter (Income, Employment, Payroll, Excise, Estate,
        Gift,
      </At>
      <At x={48} y={517} size={8} fit={242.8}>
        Whistleblower, Practitioner Discipline, PLR, FOIA, Civil Penalty, Sec.
      </At>
      <At x={59} y={526} size={8} fit={220.1}>
        4980H Shared Responsibility Payment, etc.) (see instructions)
      </At>
      <At x={342} y={511} size={8} fit={64.9}>Tax Form Number</At>
      <At x={312} y={521} size={8} fit={124.8}>
        (1040, 941, 720, etc.) (if applicable)
      </At>
      <At x={453} y={511} size={8} fit={116.8}>
        Year(s) or Period(s) (if applicable)
      </At>
      <At x={481} y={521} size={8} fit={60.4}>(see instructions)</At>

      {/* line 3 rows — 540/564/588 rules, columns split at 302.2 and 446.2 */}
      {[540, 564, 588].map((ry, i) => (
        <div key={ry}>
          <Field
            x={40}
            y={ry + 5}
            w={258}
            h={13}
            value={i === 0 ? v?.matterDescription ?? "ITIN Application" : ""}
            onChange={set("matterDescription")}
            disabled={disabled || i> 0}
          />
          <Field
            x={306}
            y={ry + 5}
            w={136}
            h={13}
            value={i === 0 ? v?.matterForm ?? "W7" : ""}
            onChange={set("matterForm")}
            disabled={disabled || i> 0}
          />
          <Field
            x={450}
            y={ry + 5}
            w={122}
            h={13}
            value={
              i === 0
                ? v?.matterYears ??
                  `${new Date().getFullYear() - 1}, ${new Date().getFullYear()}`
                : ""
            }
            onChange={set("matterYears")}
            disabled={disabled || i> 0}
          />
        </div>
      ))}

      {/* =============================== 4 ================================ */}
      <At x={46} y={613} size={8} bold>4</At>
      <At x={65} y={613} size={8} bold fit={270.8}>
        Specific use not recorded on the Centralized Authorization File (CAF).
      </At>
      <At x={339} y={614} size={8} fit={222.6}>
        If the power of attorney is for a specific use not recorded on
      </At>
      <At x={65} y={625} size={8} fit={92.4}>CAF,  check this box. See</At>
      <At x={160} y={625} size={8} italic fit={152.2}>
        Line 4. Specific Use Not Recorded on CAF
      </At>
      <At x={314} y={625} size={8}>
        in the instructions <Leader n={14} />
      </At>
      <Arrow x={554} y={626} size={5} />
      <Cb x={568} y={625} />

      {/* =============================== 5a =============================== */}
      <At x={46} y={639} size={8} bold>5</At>
      <At x={50} y={639} size={8} bold>a</At>
      <At x={65} y={639} size={8} bold fit={101.8}>
        Additional acts authorized.
      </At>
      <At x={169} y={639} size={8} fit={393.3}>
        In addition to the acts listed on line 3 above, I authorize my
        representative(s) to perform the following acts (see
      </At>
      <At x={65} y={650} size={8} fit={157.9}>
        instructions for line 5a for more information):
      </At>
      <Cb x={230.4} y={650} />
      <At x={241} y={650} size={8} fit={217.1}>
        Access my IRS records via an Intermediate Service Provider;
      </At>

      <Cb x={64.8} y={662} />
      <At x={76} y={662} size={8} fit={128.6}>
        Authorize disclosure to third parties;
      </At>
      <Cb x={230.4} y={662} />
      <At x={241} y={662} size={8} fit={125.5}>
        Substitute or add representative(s);
      </At>
      <Cb x={381.6} y={662} checked={v?.signReturn ?? true} />
      <At x={392} y={662} size={8} fit={48.4}>Sign a return;</At>
      <Field
        x={444}
        y={660}
        w={130}
        h={11}
        name="signReturnNote1"
        style={{ fontWeight: 600 }}
        value={v?.signReturnNote1 ?? "This power of attorney is being"}
        onChange={set("signReturnNote1")}
        disabled={disabled}
      />
      <Field
        x={65}
        y={672}
        w={509}
        h={11}
        name="signReturnNote2"
        style={{ fontWeight: 600 }}
        value={
          v?.signReturnNote2 ??
          "filed pursuant to Regulations Section 1.6012-1(a)(5) by reason of continuous absence from the USA."
        }
        onChange={set("signReturnNote2")}
        disabled={disabled}
      />

      <Cb x={64.8} y={710} />
      <At x={76} y={710} size={8} fit={79}>Other acts authorized:</At>
      <Field x={160} y={708} w={414} h={11} disabled />

      {/* ============================= footer ============================= */}
      <At x={36} y={746} size={8} bold fit={283.9}>
        For Privacy Act and Paperwork Reduction Act Notice, see the
        instructions.
      </At>
      <At x={378} y={747} size={7} fit={50.7}>Cat. No. 11980J</At>
      <At x={492} y={747} size={7} fit={16.1}>Form</At>
      <At x={510} y={744} size={10} bold fit={22.3} leading={10}>
        2848
      </At>
      <At x={533.5} y={747} size={7} fit={42.5}>(Rev. 1-2021)</At>
    </>
  );
};

export default F2848Page1;
